import {
  ChakraBox,
  ChakraFlex,
  EquipUIFireflyV2Theme,
  FAIconName,
  Icon,
  Tag,
  TagColor,
  Text,
} from '@equip.health/ui';
import { RefCarePlanProblemResponse } from '~/lib/types/carePlan/RefCarePlanProblemResponse';

const { neutral, primary, secondary } = EquipUIFireflyV2Theme.colors;

export type AddMultiProblemSelectedRowProps = {
  idPrefix: string;
  isChecked: boolean;
  isExistingInCarePlan: boolean;
  isFocused: boolean;
  isRecommended?: boolean;
  onMouseEnter?: () => void;
  onToggle: () => void;
  problem: RefCarePlanProblemResponse;
  showBodySystem: boolean;
};

export const AddMultiProblemSelectedRow = ({
  idPrefix,
  isChecked,
  isExistingInCarePlan,
  isFocused,
  isRecommended = false,
  onMouseEnter,
  onToggle,
  problem,
  showBodySystem,
}: AddMultiProblemSelectedRowProps) => {
  const isSelected = !isExistingInCarePlan && isChecked;
  const isFocusHighlighted = !isExistingInCarePlan && !isChecked && isFocused;

  let backgroundColor: string;
  if (isExistingInCarePlan) {
    backgroundColor = neutral.background.secondary;
  } else if (isSelected) {
    backgroundColor = primary[10];
  } else if (isFocusHighlighted) {
    backgroundColor = secondary[5];
  } else {
    backgroundColor = 'neutral.white';
  }

  let borderColor: string;
  if (isSelected) {
    borderColor = primary[100];
  } else if (isExistingInCarePlan) {
    // Design calls for no stroke; matching the fill keeps the row the same height as
    // its neighbours without needing a separate borderWidth/padding branch.
    borderColor = backgroundColor;
  } else {
    borderColor = neutral.line;
  }
  const borderWidth = isSelected ? '2px' : '1px';
  const padding = isSelected ? '17px' : '18px';

  return (
    <ChakraFlex
      _focus={{ boxShadow: 'none', outline: 'none' }}
      _focusVisible={{ boxShadow: 'none', outline: 'none' }}
      alignItems="center"
      aria-checked={isChecked}
      aria-disabled={isExistingInCarePlan}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      borderRadius="12px"
      borderWidth={borderWidth}
      cursor={isExistingInCarePlan ? 'not-allowed' : 'pointer'}
      gap="12px"
      id={`${idPrefix}-row-${problem.code}`}
      onClick={isExistingInCarePlan ? undefined : onToggle}
      onMouseEnter={isExistingInCarePlan ? undefined : onMouseEnter}
      padding={padding}
      role="checkbox"
      tabIndex={-1}
    >
      {showBodySystem && (
        <Text
          _firstLetter={{ textTransform: 'uppercase' }}
          color="neutral.secondary"
          flexShrink={0}
          textTransform="lowercase"
          variant="body"
          width="200px"
        >
          {problem.bodySystem || ''}
        </Text>
      )}
      <Text variant="bodyMedium">{problem.label}</Text>
      {isRecommended && !isExistingInCarePlan && (
        <Tag
          color={TagColor.periwinkle}
          id={`${idPrefix}-recommended-${problem.code}`}
          value="Recommended"
        />
      )}
      <ChakraBox flex="1" />
      {isExistingInCarePlan && (
        <ChakraFlex alignItems="center" color="neutral.secondary" gap="6px">
          <Icon name={FAIconName.checkRegular} />
          {/* Text sets its own color, so the parent's `color` does not cascade to it */}
          <Text color="neutral.secondary" variant="caption">
            Already in care plan
          </Text>
        </ChakraFlex>
      )}
      {isSelected && (
        <Icon color={primary[100]} name={FAIconName.checkRegular} />
      )}
    </ChakraFlex>
  );
};
